import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { farmbaseApi, targetApi } from '../services/api';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import { normalizeTarget } from '../store/targetStore';
import FarmbaseForm from '../components/studio/FarmbaseForm';
import TargetForm from '../components/studio/TargetForm';
import { geometryCentroid } from '../lib/geo';
import { formatMGRS } from '../lib/mgrs';
import { apiErrorMessage } from '../lib/apiError';
import { TARGET_STATUS_META, TARGET_TYPE_META, type Farmbase, type Target } from '../types/domain';

type Tab = 'farmbases' | 'targets';

function geomSummary(g: any): string {
  // Geometry may be NESTED on the raw DTO (farmbase) or FLAT on a normalized target.
  const geo = g?.geometry ?? g;
  if (!geo?.shape) return 'no geometry';
  const c = geometryCentroid(geo);
  return c ? `${geo.shape} · ${formatMGRS(c.lat, c.lon)}` : geo.shape;
}

export default function LocationStudioPage() {
  const { user } = useAuthStore();
  const { isHQ } = useUnitStore();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('farmbases');
  const [editing, setEditing] = useState<{ kind: Tab; row: Farmbase | Target | null } | null>(null);

  const userIsHQ = isHQ(user?.unitId ?? null);

  const farmbasesQ = useQuery({
    queryKey: ['studio-farmbases'],
    queryFn: async () => (await farmbaseApi.getAll()).data.farmbases as Farmbase[],
    enabled: userIsHQ,
  });
  const targetsQ = useQuery({
    queryKey: ['studio-targets'],
    queryFn: async () => ((await targetApi.getAll({ includeExpired: true })).data.targets || []).map(normalizeTarget) as Target[],
    enabled: userIsHQ,
  });

  useEffect(() => {
    if (userIsHQ) {
      const store = useUnitStore.getState();
      if (store.units.length === 0) store.fetchUnits();
    }
  }, [userIsHQ]);

  if (!userIsHQ) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-display font-bold text-gray-100">Location Studio</h1>
        <p className="text-gray-500 mt-2 text-sm">Superuser (Brigade HQ) only.</p>
      </div>
    );
  }

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['studio-farmbases'] });
    qc.invalidateQueries({ queryKey: ['studio-targets'] });
    qc.invalidateQueries({ queryKey: ['targets'] });
    qc.invalidateQueries({ queryKey: ['geofences'] });
    setEditing(null);
  };

  const del = async (kind: Tab, id: string) => {
    try {
      if (kind === 'farmbases') await farmbaseApi.delete(id);
      else await targetApi.delete(id);
      toast.success('Deleted');
      refresh();
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Delete failed'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg font-display font-bold text-gray-100">Location Studio</h1>
          <p className="text-gray-500 text-[11px]">Draw farmbases &amp; targets — POINT / LINE / BOX geometry</p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ kind: tab, row: null })}
          className="px-4 py-1.5 min-h-11 lg:min-h-0 rounded-lg text-sm font-semibold bg-command-accent text-command-bg touch-manipulation"
        >
          ＋ New {tab === 'farmbases' ? 'Farmbase' : 'Target'}
        </button>
      </div>

      <div className="flex gap-2">
        {(['farmbases', 'targets'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={clsx(
              'px-4 py-1.5 min-h-11 lg:min-h-0 rounded-lg text-sm font-semibold border touch-manipulation',
              tab === t ? 'bg-command-accent/20 text-command-accent border-command-accent/40' : 'text-gray-400 border-command-border'
            )}
          >
            {t === 'farmbases' ? 'Farmbases' : 'Targets'}
          </button>
        ))}
      </div>

      {tab === 'farmbases' ? (
        <div className="rounded-xl border border-command-border bg-command-panel divide-y divide-command-border/60">
          {(farmbasesQ.data ?? []).map((fb) => (
            <div key={fb.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-100">{fb.name}</div>
                <div className="text-[11px] text-gray-500 break-words">
                  {fb.commanderName || '—'} · O{fb.manpowerOfficers ?? 0}/J{fb.manpowerJCOs ?? 0}/OR{fb.manpowerORs ?? 0} · {geomSummary(fb)}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button type="button" onClick={() => setEditing({ kind: 'farmbases', row: fb })} className="inline-flex items-center min-h-11 lg:min-h-0 px-2 text-xs text-command-accent touch-manipulation">Edit</button>
                <button type="button" onClick={() => del('farmbases', fb.id)} className="inline-flex items-center min-h-11 lg:min-h-0 px-2 text-xs text-red-400 touch-manipulation">Delete</button>
              </div>
            </div>
          ))}
          {(farmbasesQ.data ?? []).length === 0 && <div className="px-4 py-8 text-center text-gray-500 text-sm">No farmbases yet.</div>}
        </div>
      ) : (
        <div className="rounded-xl border border-command-border bg-command-panel divide-y divide-command-border/60">
          {(targetsQ.data ?? []).map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-100 flex items-center gap-2 flex-wrap">
                  <span>{TARGET_TYPE_META[t.type]?.icon}</span>
                  {t.name}
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ color: TARGET_STATUS_META[t.status]?.color, backgroundColor: `${TARGET_STATUS_META[t.status]?.color}22` }}>
                    {TARGET_STATUS_META[t.status]?.label ?? t.status}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500">
                  {TARGET_TYPE_META[t.type]?.label} · enemy {t.enemyManpower ?? '—'} · {geomSummary(t)}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button type="button" onClick={() => setEditing({ kind: 'targets', row: t })} className="inline-flex items-center min-h-11 lg:min-h-0 px-2 text-xs text-command-accent touch-manipulation">Edit</button>
                <button type="button" onClick={() => del('targets', t.id)} className="inline-flex items-center min-h-11 lg:min-h-0 px-2 text-xs text-red-400 touch-manipulation">Delete</button>
              </div>
            </div>
          ))}
          {(targetsQ.data ?? []).length === 0 && <div className="px-4 py-8 text-center text-gray-500 text-sm">No targets yet.</div>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[2000] bg-black/60 flex items-start justify-center overflow-auto overscroll-contain p-0 sm:p-4">
          <div className="w-full max-w-3xl bg-command-panel border border-command-border rounded-none sm:rounded-2xl p-4 sm:p-5 my-0 sm:my-8 min-h-full sm:min-h-0">
            <h2 className="text-base font-semibold text-gray-100 mb-3">
              {editing.row ? 'Edit' : 'New'} {editing.kind === 'farmbases' ? 'Farmbase' : 'Target'}
            </h2>
            {editing.kind === 'farmbases' ? (
              <FarmbaseForm initial={editing.row as Farmbase} onSaved={refresh} onCancel={() => setEditing(null)} />
            ) : (
              <TargetForm initial={editing.row as Target} onSaved={refresh} onCancel={() => setEditing(null)} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
